import React, { useEffect, useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import getWorkcellColor from "../utils/getWorkcellColor";
import { fetchDailyTurnover, fetchDailyHoursSummary } from "../api";
import { useTranslation } from 'react-i18next';
import { useCustomerCurrency } from "../context/CustomerSettingsContext";

const DailyTurnoverTable = ({ date, onLoadingChange }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatCurrency } = useCustomerCurrency();
  const [opData, setOpData] = useState([]);
  const [hrData, setHrData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    if (!date) {
      setOpData([]);
      setHrData([]);
      setIsLoading(false);
      onLoadingChange?.(false);
      return;
    }
    setIsLoading(true);
    onLoadingChange?.(true);
    fetchDailyTurnover(date)
      .then((res) => { setOpData(res || []); })
      .catch(() => { setOpData([]); })
      .finally(() => { setIsLoading(false); });
    // fetch summary separately
    fetchDailyHoursSummary(date)
      .then((res) => { setHrData(res || []); })
      .catch(() => { setHrData([]); })
      .finally(() => {
        onLoadingChange?.(false);
      });
  }, [date]);
  // Group operation turnover by workcell
  const opMap = {};
  opData.forEach(row => {
    if (!opMap[row.workcell_id]) opMap[row.workcell_id] = 0;
    opMap[row.workcell_id] += row.operation_turnover || 0;
  });
  // Group hourly turnover by workcell (from summary)
  const hrMap = {};
  hrData.forEach(row => {
    hrMap[row.workcell_id] = row.internal_turnover || 0;
  });
  // Workcell names and color order
  const workcellNames = {};
  hrData.forEach(row => { workcellNames[row.workcell_id] = row.workcell_name; });
  opData.forEach(row => { workcellNames[row.workcell_id] = row.workcell_name; });
  const allWorkcellIds = Object.keys(workcellNames).sort((a, b) => a - b);
  // Build rows
  const rows = allWorkcellIds.map(id => ({
    workcell_id: id,
    workcell_name: workcellNames[id],
    op: opMap[id] || 0,
    hr: hrMap[id] || 0,
    total: (opMap[id] || 0) + (hrMap[id] || 0)
  }));
  // Sort rows by total turnover descending
  const sortedRows = [...rows].sort((a, b) => b.total - a.total);
  const totalOp = rows.reduce((sum, r) => sum + r.op, 0);
  const totalHr = rows.reduce((sum, r) => sum + r.hr, 0);
  const totalTotal = rows.reduce((sum, r) => sum + r.total, 0);

  if (isLoading) return <div>{t('common.loading')}</div>;
  if (rows.length === 0) return <div>{t('common.noData')}</div>;
  return (
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell>{t('daily.overview.turnoverTable.workcell')}</TableCell>
          <TableCell align="right">{t('daily.overview.turnoverTable.operationTurnover')}</TableCell>
          <TableCell align="right">{t('daily.overview.turnoverTable.hourlyTurnover')}</TableCell>
          <TableCell align="right">{t('daily.overview.turnoverTable.totalTurnover')}</TableCell>
          <TableCell sx={{ width: '10rem' }} />
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedRows.map(row => (
          <TableRow key={row.workcell_id}>
            <TableCell component="th" scope="row" sx={{ color: getWorkcellColor(row.workcell_id, theme.palette.chart.colors, allWorkcellIds), fontWeight: 'bold' }}>{row.workcell_name}</TableCell>
            <TableCell align="right">{formatCurrency(row.op)}</TableCell>
            <TableCell align="right">{formatCurrency(row.hr)}</TableCell>
            <TableCell align="right">{formatCurrency(row.total)}</TableCell>
            <TableCell align="right">
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate(`/chart/workcell/${row.workcell_id}/daily/${date}`)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {t('common.viewDetails')}
                </Button>
              </TableCell>
          </TableRow>
        ))}
        <TableRow sx={{ fontWeight: 'bold', backgroundColor: theme.palette.action.hover }}>
          <TableCell component="th" scope="row">{t('common.total')}</TableCell>
          <TableCell align="right">{formatCurrency(totalOp)}</TableCell>
          <TableCell align="right">{formatCurrency(totalHr)}</TableCell>
          <TableCell align="right">{formatCurrency(totalTotal)}</TableCell>
          <TableCell sx={{ width: '10rem' }} />
        </TableRow>
      </TableBody>
    </Table>
  );
};

export default DailyTurnoverTable;
